import { motion } from 'motion/react'
import { mq, pxToRem, sizes } from 'src/theme'
import styled, { css } from 'styled-components'

import FooterLogo from '../FooterLogo'
import Icon from '../Icon'
import LangSwitch from '../LangSwitch'
import { _Link as LangSwitchLink, Trigger } from '../LangSwitch/styled'
import Link from '../Link'

export const Container = styled(motion.footer)<{ $color?: string }>`
  --padding-block-end: calc(var(--g_height__bottom, ${pxToRem(48)}));
  --background: ${({ $color, theme }) => $color ?? theme.colors.black};
  --color: ${({ theme }) => theme.colors.white};
  padding-block-start: ${pxToRem(40)};
  padding-block-end: var(--padding-block-end);
  color: var(--color);
  background: var(--background);
  @media ${mq(sizes.tablet)} {
    --padding-block-end: 0;
  }
`
export const Top = styled.div<{ $overflowHidden?: boolean }>`
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  max-width: calc(100% - ${pxToRem(16)} * 2);
  margin-inline: auto;
  overflow: ${({ $overflowHidden }) =>
    $overflowHidden ? 'hidden' : 'visible'};
  @media ${mq(sizes.desktop)} {
    max-width: calc(100% - ${pxToRem(32)} * 2);
    flex-flow: row nowrap;
    gap: ${pxToRem(32)};
  }
`
export const LangSwitchWrapper = styled(motion.div)`
  position: relative;
  z-index: 5;
  display: flex;
  @media ${mq(sizes.desktop)} {
    order: 2;
    margin-inline-start: auto;
  }
`
export const _LangSwitch = styled(LangSwitch)<{ $color?: string }>`
  --lang_color: ${({ theme }) => theme.colors.white} !important;
  --lang_background: ${({ $color }) => $color ?? 'transparent'} !important;
  --lang_border-color: transparent !important;
  & ${Trigger}, & ${LangSwitchLink} {
    padding-inline: 0;
  }
`
export const BackWrapper = styled(motion.div)`
  display: flex;
  margin-inline-start: auto;
  @media ${mq(sizes.desktop)} {
    order: 3;
    margin-inline-start: unset;
  }
`
export const Back = styled(Link)`
  color: inherit;
`
export const BackIcon = styled(Icon)`
  flex: 0 0 auto;
  aspect-ratio: 1;
  width: ${pxToRem(32)};
`
export const Socials = styled.nav`
  ${({ theme }) => theme.textStyles.m375_bodySmall}
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: ${pxToRem(8)} ${pxToRem(16)};
  padding-block: ${pxToRem(32)};
  width: 100%;
  & ul {
    list-style: none;
    display: flex;
    align-items: center;
    gap: ${pxToRem(24)};
    margin-inline-start: auto;
  }
  & li {
    display: flex;
    align-items: center;
  }
  @media ${mq(sizes.desktop)} {
    flex-flow: row nowrap;
    padding-block: 0;
    margin-block-start: 0;
    border-block: 0;
    width: auto;
    order: 1;
  }
`
export const SeparatorMobile = styled(motion.div)`
  height: 1px;
  width: 100%;
  margin-top: ${pxToRem(32)};
  background-color: ${({ theme }) => theme.colors.white};
  opacity: 0.5;
  transform-origin: left center;
  @media ${mq(sizes.desktop)} {
    display: none;
  }
`
export const Separator = styled(motion.div)`
  height: 1px;
  width: 100%;
  max-width: calc(100% - ${pxToRem(16)} * 2);
  margin-inline: auto;
  background-color: ${({ theme }) => theme.colors.white};
  opacity: 0.5;
  transform-origin: left center;
  @media ${mq(sizes.desktop)} {
    max-width: calc(100% - ${pxToRem(32)} * 2);
    margin-block-start: ${pxToRem(24)};
  }
`
// it's only the fucking Middle on mobile now, I hate designers (and I was just about to standardize with "Rows" and "Cols")
export const Middle = styled.div`
  width: 100%;
  max-width: calc(100% - ${pxToRem(16)} * 2);
  margin-inline: auto;
  @media ${mq(sizes.desktop)} {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    gap: ${pxToRem(16)} ${pxToRem(16)};
    max-width: calc(100% - ${pxToRem(32)} * 2);
    padding-block: ${pxToRem(24)};
  }
`
export const Social = styled(Link)`
  color: inherit;
  display: flex;
  align-items: center;
`
export const SocialTitle = styled(motion.span)`
  white-space: nowrap;
`
export const SocialIcon = styled(Icon)`
  flex: 0 0 auto;
  aspect-ratio: 1;
  width: ${pxToRem(16)};
`
export const Links = styled.nav`
  ${({ theme }) => theme.textStyles.m375_bodySmall}
  margin-block-start: ${pxToRem(48)};
  & ul {
    display: flex;
    flex-flow: column;
    gap: ${pxToRem(24)};
    list-style: none;
  }
  @media ${mq(sizes.desktop)} {
    margin-block-start: 0;
    & ul {
      display: flex;
      flex-flow: row wrap;
    }
  }
`

export const LinksContainer = styled(motion.ul)`
  overflow: hidden;
`

export const _Link = styled(Link)`
  color: inherit;

  ${({ href }) =>
    href &&
    css`
      &:focus,
      &:hover {
        text-decoration: underline;
      }
    `};
`
export const Bottom = styled.div`
  @media ${mq(sizes.desktop)} {
    display: flex;
    flex-flow: row nowrap;
    padding-inline-start: ${pxToRem(32)};
  }
`
export const CopyrightWrapper = styled.div`
  width: 100%;
  max-width: calc(100% - ${pxToRem(16)} * 2);
  margin-inline: auto;
  margin-block-start: ${pxToRem(80)};
  overflow: hidden;
  @media ${mq(sizes.desktop)} {
    align-self: flex-end;
    margin-block-start: 0;
    max-width: none;
    margin-inline: 0;
    margin-block-end: ${pxToRem(40)};
  }
`
export const Copyright = styled(motion.p)`
  ${({ theme }) => theme.textStyles.m375_bodySmall}
  font-size: ${pxToRem(12)};
`

export const LogoWrapper = styled(motion.div)`
  --logo-footer-motion-scale: 0.75;
  margin-block-start: ${pxToRem(75)};
  transform-origin: right bottom;
  @media ${mq(sizes.desktop)} {
    --logo-footer-motion-scale: 1.55;
    flex: 0 0 auto;
    width: 50%;
  }
`

export const _FooterLogo = styled(FooterLogo)`
  ${({ theme }) => theme.textStyles.m375_bodySmall}
`
